import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  Archive,
  CheckCircle2,
  Cloud,
  Database,
  Download,
  FileText,
  FolderOpen,
  HardDrive,
  Loader2,
  RefreshCcw,
  Trash2,
  Upload,
  Wallet,
  X,
  Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { APIError, apiRequest, resolveAPIBaseURL } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export const Route = createFileRoute("/storage")({
  head: () => ({ meta: [{ title: "Storage · GENRL" }] }),
  component: StoragePage,
});

const STORAGE_FILE_TRANSFER_PROVIDER_SLUGS = new Set(["hyperstack"]);
const STAGING_PROVIDER_LABEL = "staging";
const BYTES_PER_GB = 1024 ** 3;
const MANAGED_STORAGE_ROUTE_ID = "genrl::managed-storage";
const MANAGED_STORAGE_LABEL = "GENRL S3";
const MANAGED_STORAGE_DETAIL = "Object storage through the GENRL account";
const MANAGED_STORAGE_ENVIRONMENT = "managed-global";
const EMPTY_STORAGE_ROUTE: StorageRouteOption = {
  id: "",
  routeID: "",
  sourceSlug: "",
  providerSlug: "",
  label: "No storage provider",
  detail: "Connect a storage-capable provider",
  driver: "provider",
};

type StorageRouteOption = {
  id: string;
  routeID: string;
  sourceSlug: string;
  providerSlug: string;
  label: string;
  detail: string;
  driver: string;
  auto?: boolean;
  managed?: boolean;
};

type StorageKind = {
  id: string;
  volumeType: string;
  label: string;
  detail: string;
  pricePerGBHour: number;
  icon: LucideIcon;
};

type StorageEnvironment = {
  name: string;
  region: string;
};

type StorageVolume = {
  id: string;
  providerVolumeID: string;
  name: string;
  status: string;
  sizeGb: number;
  type: string;
  typeLabel: string;
  provider: string;
  providerSlug: string;
  sourceSlug: string;
  routeID: string;
  region: string;
  environment: string;
  pricePerHour: number;
  pricePerGBHour: number;
  files: number;
  capacityBytes: number;
  usedBytes: number;
  availableBytes: number;
  createdAt: string;
  updatedAt: string;
  updatedAtMs: number;
};

type BackendVolume = {
  id?: number | string;
  provider_volume_id?: string;
  name?: string;
  status?: string;
  size_gb?: number;
  size?: number;
  volume_type?: string;
  provider?: string;
  provider_slug?: string;
  source_slug?: string;
  route_id?: string;
  region_name?: string;
  environment_name?: string;
  price_per_hour?: number;
  price_per_gb_hour?: number;
  file_count?: number;
  files_count?: number;
  used_bytes?: number;
  available_bytes?: number;
  used_gb?: number;
  available_gb?: number;
  created_at?: string;
  updated_at?: string;
};

type BackendCreditBalance = {
  available_credit_micros?: number;
};

type BackendStorageItem = {
  id?: string;
  filename?: string;
  provider?: string;
  provider_path?: string;
  provider_status?: string;
  provider_error?: string;
  size_bytes?: number;
  content_type?: string;
  created_at?: string;
  updated_at?: string;
  download_url?: string;
};

type StorageFile = {
  id: string;
  filename: string;
  provider: string;
  providerPath: string;
  providerStatus: string;
  providerError: string;
  sizeBytes: number;
  contentType: string;
  createdAt: string;
  updatedAt: string;
  downloadURL: string;
};

type ProviderSourcePublic = {
  source_slug?: string;
  provider_slug?: string;
  route_id?: string;
  driver?: string;
  display_name?: string;
  base_url?: string;
  provision_base_url?: string;
  active?: boolean;
};

type ProProviderStorageRoute = {
  id?: string;
  provider_slug?: string;
  provider_name?: string;
  display_name?: string;
  status?: string;
  has_credential?: boolean;
};

type BackendStorageRoute = {
  route_id?: string;
  source_slug?: string;
  provider_slug?: string;
  label?: string;
  detail?: string;
  driver?: string;
  managed?: boolean;
  genrl_managed?: boolean;
};

type WeightUploadSession = {
  upload_url?: string;
  artifact_ref?: string;
  chunk_size_bytes?: number;
  max_size_bytes?: number;
};

type WeightUploadStatus = {
  received_bytes?: number;
  total_bytes?: number;
  completed?: boolean;
};

const DEFAULT_KINDS: StorageKind[] = [
  {
    id: "object",
    volumeType: "object",
    label: "Object storage",
    detail: "General files and datasets",
    pricePerGBHour: 0.000018,
    icon: Cloud,
  },
  {
    id: "ssd",
    volumeType: "ssd",
    label: "Fast block",
    detail: "Reusable job volumes",
    pricePerGBHour: 0.00005,
    icon: HardDrive,
  },
  {
    id: "nvme",
    volumeType: "nvme",
    label: "NVMe scratch",
    detail: "Fastest active runs",
    pricePerGBHour: 0.00009,
    icon: Zap,
  },
  {
    id: "archive",
    volumeType: "archive",
    label: "Archive",
    detail: "Lowest cost retention",
    pricePerGBHour: 0.00001,
    icon: Archive,
  },
];

function StoragePage() {
  const [routes, setRoutes] = useState<StorageRouteOption[]>([]);
  const [selectedRouteID, setSelectedRouteID] = useState("");
  const [volumes, setVolumes] = useState<StorageVolume[]>([]);
  const [kinds, setKinds] = useState<StorageKind[]>(DEFAULT_KINDS);
  const [environments, setEnvironments] = useState<StorageEnvironment[]>([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [availableCreditMicros, setAvailableCreditMicros] = useState<number | null>(null);
  const [drawerVolume, setDrawerVolume] = useState<StorageVolume | null>(null);
  const [closeVolumeTarget, setCloseVolumeTarget] = useState<StorageVolume | null>(null);
  const [closingVolumeID, setClosingVolumeID] = useState("");
  const [name, setName] = useState("project-files");
  const [sizeGb, setSizeGb] = useState(100);
  const [selectedType, setSelectedType] = useState("object");

  const selectedRoute = useMemo(
    () => routes.find((route) => route.id === selectedRouteID) || routes[0] || EMPTY_STORAGE_ROUTE,
    [routes, selectedRouteID],
  );
  const activeVolumes = useMemo(
    () => volumes.filter((volume) => isLiveStorageVolumeStatus(volume.status)),
    [volumes],
  );
  const latestVolumes = useMemo(
    () => [...activeVolumes].sort((a, b) => b.updatedAtMs - a.updatedAtMs),
    [activeVolumes],
  );
  const totalCapacityBytes = activeVolumes.reduce((sum, volume) => sum + volume.capacityBytes, 0);
  const totalUsedBytes = activeVolumes.reduce((sum, volume) => sum + volume.usedBytes, 0);
  const totalAvailableBytes = activeVolumes.reduce((sum, volume) => sum + volume.availableBytes, 0);
  const totalFiles = activeVolumes.reduce((sum, volume) => sum + volume.files, 0);
  const hourlyCost = activeVolumes.reduce((sum, volume) => sum + volume.pricePerHour, 0);
  const selectedKind = kinds.find((kind) => kind.id === selectedType) || kinds[0] || DEFAULT_KINDS[0];
  const estimatedMonthly = Math.max(0, sizeGb * selectedKind.pricePerGBHour * 24 * 30);
  const displayEnvironments = useMemo(
    () => storageEnvironmentsForRoute(selectedRoute, environments),
    [selectedRoute, environments],
  );
  const selectedEnvironmentName = selectedEnvironment || chooseEnvironment(displayEnvironments);
  const creditActionsDisabled = availableCreditMicros !== null && availableCreditMicros <= 0;
  const canCreateVolume = Boolean(selectedRoute.routeID && storageEnvironmentForRoute(selectedRoute, selectedEnvironmentName) && !creditActionsDisabled);

  async function refreshStorage(showSpinner = true) {
    if (showSpinner) setRefreshing(true);
    try {
      const [nextRoutes, nextAvailableCreditMicros] = await Promise.all([
        loadStorageRoutes(),
        loadAvailableStorageCreditMicros().catch(() => null),
      ]);
      if (nextAvailableCreditMicros !== null) {
        setAvailableCreditMicros(nextAvailableCreditMicros);
      }
      if (nextRoutes.length === 0) {
        setRoutes([]);
        setSelectedRouteID("");
        setVolumes([]);
        setKinds([]);
        setEnvironments([]);
        setSelectedEnvironment("");
        return;
      }
      const nextSelectedRouteID = nextRoutes.some((route) => route.id === selectedRouteID)
        ? selectedRouteID
        : nextRoutes[0]?.id || "";
      const nextSelectedRoute = nextRoutes.find((route) => route.id === nextSelectedRouteID) || nextRoutes[0] || EMPTY_STORAGE_ROUTE;
      const query = storageRouteQuery(nextSelectedRoute);

      setRoutes(nextRoutes);
      if (nextSelectedRouteID !== selectedRouteID) setSelectedRouteID(nextSelectedRouteID);

      const [volumePayload, typePayload, environmentPayload] = await Promise.all([
        apiRequest<{ volumes?: BackendVolume[] }>(`/api/v1/storage/volumes${query}`, {
          timeoutMs: 20_000,
          cacheMaxAgeMs: 15_000,
        }),
        apiRequest<{
          volume_types?: string[];
          volume_type_pricing?: Array<{ volume_type?: string; price_per_gb_hour?: number }>;
        }>(`/api/v1/storage/volume-types${query}`, {
          timeoutMs: 20_000,
          cacheMaxAgeMs: 60_000,
        }),
        apiRequest<{ environments?: Array<{ name?: string; region?: string }> }>(
          `/api/v1/storage/environments${query}`,
          { timeoutMs: 20_000, cacheMaxAgeMs: 60_000 },
        ),
      ]);

      const nextKinds = managedObjectStorageKinds(mapStorageKinds(typePayload, nextSelectedRoute.label));
      setKinds(nextKinds);
      if (!nextKinds.some((kind) => kind.id === selectedType)) {
        setSelectedType(nextKinds[0]?.id || "object");
      }

      const fetchedEnvironments = (environmentPayload.environments || [])
        .map((row) => ({
          name: String(row.name || "").trim(),
          region: String(row.region || "").trim(),
        }))
        .filter((row) => row.name);
      const nextEnvironments = storageEnvironmentsForRoute(nextSelectedRoute, fetchedEnvironments);
      setEnvironments(nextEnvironments);
      setSelectedEnvironment((current) => {
        if (nextEnvironments.some((env) => env.name === current)) return current;
        return chooseEnvironment(nextEnvironments);
      });

      const rows = await Promise.all(
        (volumePayload.volumes || []).map(async (row) => {
          const volume = mapBackendVolume(row, nextKinds, nextSelectedRoute);
          try {
            const data = await apiRequest<{ count?: number; total_bytes?: number }>(`/api/v1/storage/volumes/${volume.id}/data`, {
              timeoutMs: 10_000,
              cacheMaxAgeMs: 15_000,
            });
            volume.files = Number(data.count || 0);
            applyVolumeUsage(volume, Number(data.total_bytes || 0));
          } catch {
            volume.files = volume.files || 0;
          }
          return volume;
        }),
      );
      rows.sort((a, b) => b.updatedAtMs - a.updatedAtMs);
      setVolumes(rows);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Storage is temporarily unavailable."));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    void refreshStorage(false);
    const timer = window.setInterval(() => void refreshStorage(false), 30_000);
    return () => window.clearInterval(timer);
  }, [selectedRouteID]);

  async function createVolume() {
    if (creditActionsDisabled) {
      toast.error("Purchase credits before opening a new volume.");
      return;
    }
    const cleanName = name.trim();
    if (!cleanName) {
      toast.error("Name this storage space first.");
      return;
    }
    const cleanSize = Math.max(1, Math.floor(Number(sizeGb || 0)));
    const environmentName = storageEnvironmentForRoute(selectedRoute, selectedEnvironmentName);
    if (!environmentName) {
      toast.error("Select a storage environment first.");
      return;
    }
    setCreating(true);
    try {
      const body: Record<string, unknown> = {
        name: cleanName,
        size_gb: cleanSize,
        volume_type: selectedKind.volumeType,
        environment_name: environmentName,
      };
      if (selectedRoute.routeID) body.route_id = selectedRoute.routeID;
      if (selectedRoute.sourceSlug) body.source_slug = selectedRoute.sourceSlug;
      if (selectedRoute.providerSlug) body.provider_slug = selectedRoute.providerSlug;

      await apiRequest("/api/v1/storage/volumes", {
        method: "POST",
        body,
        timeoutMs: 30_000,
        cache: "no-store",
      });
      toast.success("Storage volume created.");
      await refreshStorage(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Storage could not be created."));
    } finally {
      setCreating(false);
    }
  }

  async function closeVolume(volume: StorageVolume) {
    if (!volume.id || closingVolumeID) return;
    setClosingVolumeID(volume.id);
    try {
      await apiRequest(`/api/v1/storage/volumes/${encodeURIComponent(volume.id)}${storageVolumeRouteQuery(volume)}`, {
        method: "DELETE",
        timeoutMs: 30_000,
        cache: "no-store",
      });
      toast.success("Storage volume closed.");
      setCloseVolumeTarget(null);
      setDrawerVolume((current) => (current && sameStorageVolume(current, volume) ? null : current));
      setVolumes((current) => current.filter((item) => !sameStorageVolume(item, volume)));
      await refreshStorage(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Storage volume could not be closed."));
    } finally {
      setClosingVolumeID("");
    }
  }

  return (
    <>
      <div className="compute-wallet-page compute-storage-page">
        <div className="compute-wallet-shell compute-storage-wallet-shell">
          <main className="compute-wallet-main compute-storage-wallet-main">
            <header className="compute-wallet-header compute-storage-header">
              <div>
                <h1>STORAGE</h1>
                <p>Connect GENRL-managed S3 storage, upload files, and retrieve data from the browser.</p>
              </div>
              <div className="compute-wallet-header-actions compute-storage-header-actions">
                <button
                  type="button"
                  onClick={() => void refreshStorage()}
                  disabled={refreshing}
                  className="compute-wallet-circle-button"
                  aria-label="Refresh storage"
                  title="Refresh storage"
                >
                  <RefreshCcw className={cn("h-[18px] w-[18px]", refreshing && "animate-spin")} />
                </button>
                <div>
                  <strong>{refreshing ? "Refreshing" : "Synced"}</strong>
                  <small>Storage</small>
                </div>
              </div>
            </header>

            <section className="compute-storage-summary" aria-label="Storage summary">
              <StorageStat
                icon={HardDrive}
                label="Storage"
                value={`${formatBytes(totalUsedBytes)} / ${formatBytes(totalCapacityBytes)}`}
                hint={`${formatBytes(totalAvailableBytes)} available across ${activeVolumes.length} active volumes`}
              />
              <StorageStat icon={FileText} label="Files" value={totalFiles.toLocaleString()} hint="Stored in selected route" />
              <StorageStat icon={Cloud} label="Provider" value={selectedRoute.label} hint={selectedRoute.detail} />
              <StorageStat icon={Database} label="Hourly" value={formatUSD(hourlyCost, 4)} hint={`${formatUSD(estimatedMonthly)} est. new volume monthly`} />
            </section>

            <section className="compute-storage-grid">
              <section className="compute-storage-create-panel" aria-label="Create volume">
                <div className="compute-storage-section-header">
                  <div>
                    <span>Connect storage</span>
                    <h2>{selectedRoute.label || MANAGED_STORAGE_LABEL}</h2>
                  </div>
                  <Database className="h-5 w-5" />
                </div>

                <div className="compute-storage-field-grid">
                  <Field label="Name">
                    <input
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                    />
                  </Field>
                  <Field label="Size">
                    <div className="compute-storage-input-suffix">
                      <input
                        min={1}
                        type="number"
                        value={sizeGb}
                        onChange={(event) => setSizeGb(Number(event.target.value || 1))}
                      />
                      <small>GB</small>
                    </div>
                  </Field>
                </div>

                <div className="compute-storage-side-copy">
                  <div className="compute-storage-proof">
                    <Cloud className="h-4 w-4" />
                    <span>{selectedKind.label || "S3 object storage"} through the GENRL account.</span>
                  </div>
                </div>

                {creditActionsDisabled ? (
                  <div className="compute-storage-credit-lock">
                    <Wallet className="h-4 w-4" />
                    <div>
                      <strong>Purchase credits</strong>
                      <span>Use a purchase link before opening a new volume.</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => window.location.assign("/settings?section=billing")}
                    >
                      Manage credits in Settings
                    </button>
                  </div>
                ) : null}

                <div className="compute-storage-create-footer">
                  <div>
                    <span>Estimated monthly</span>
                    <strong>{formatUSD(estimatedMonthly)}</strong>
                    <p>{sizeGb.toLocaleString()} GB x S3 object storage</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void createVolume()}
                    disabled={creating || !canCreateVolume}
                  >
                    {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
                    {creditActionsDisabled ? "Purchase credits" : creating ? "Creating..." : "Create S3 storage"}
                  </button>
                </div>
              </section>

              <aside className="compute-storage-side-panel" aria-label="Storage route details">
                <div className="compute-storage-section-header">
                  <div>
                    <span>GENRL account</span>
                    <h2>S3 storage</h2>
                  </div>
                  <Cloud className="h-5 w-5" />
                </div>
                <div className="compute-storage-route-list">
                  <InfoRow label="Account" value="GENRL" />
                  <InfoRow label="Storage" value={MANAGED_STORAGE_LABEL} />
                  <InfoRow label="Type" value={selectedKind.label || "Object storage"} />
                  <InfoRow label="Route" value={selectedRoute.routeID || MANAGED_STORAGE_ROUTE_ID} />
                </div>
                <div className="compute-storage-side-copy">
                  <div className="compute-storage-proof">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>Uploads are staged first, then committed through the GENRL account.</span>
                  </div>
                </div>
              </aside>
            </section>

            <section className="compute-wallet-card compute-wallet-activity-card compute-storage-history" aria-label="Storage volumes">
              <div className="compute-wallet-card-header">
                <div>
                  <FolderOpen className="h-5 w-5" />
                  <h2>Volumes</h2>
                </div>
                <button
                  type="button"
                  onClick={() => void refreshStorage()}
                  disabled={refreshing}
                >
                  <RefreshCcw className={cn("h-3.5 w-3.5", refreshing && "animate-spin")} />
                  Refresh
                </button>
              </div>
              <p className="compute-storage-history-subtitle">
                {latestVolumes.length} volume{latestVolumes.length === 1 ? "" : "s"} on {selectedRoute.label}
              </p>
              {loading ? (
                <EmptyState icon={Loader2} title="Loading storage..." spin />
              ) : latestVolumes.length === 0 ? (
                <EmptyState icon={FolderOpen} title="No volumes on this route yet." />
              ) : (
                <div className="compute-storage-volume-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Volume</th>
                        <th>Provider</th>
                        <th>Environment</th>
                        <th>Used / Available</th>
                        <th>Files</th>
                        <th>Cost</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {latestVolumes.map((volume) => (
                        <tr key={`${volume.routeID}:${volume.id}`}>
                          <td>
                            <strong>{volume.name}</strong>
                            <small>{volume.providerVolumeID || volume.id} / {volume.typeLabel}</small>
                          </td>
                          <td>
                            <strong>{volume.provider}</strong>
                            <small>{volume.routeID || "auto"}</small>
                          </td>
                          <td>
                            <strong>{volume.environment}</strong>
                            <small>{volume.region}</small>
                          </td>
                          <td>
                            <StorageUsageCell volume={volume} />
                          </td>
                          <td>{volume.files.toLocaleString()}</td>
                          <td>{formatUSD(volume.pricePerHour, 4)} / h</td>
                          <td>
                            <span className={cn("compute-storage-status", statusClass(volume.status))}>
                              {formatStatus(volume.status)}
                            </span>
                          </td>
                          <td>
                            <div className="compute-storage-volume-actions">
                              <button
                                type="button"
                                onClick={() => setDrawerVolume(volume)}
                              >
                                <Upload className="h-3.5 w-3.5" />
                                Files
                              </button>
                              <button
                                type="button"
                                className="compute-storage-volume-close-button"
                                disabled={closingVolumeID === volume.id}
                                onClick={() => setCloseVolumeTarget(volume)}
                              >
                                {closingVolumeID === volume.id ? (
                                  <Loader2 className="h-3.5 w-3.5 compute-storage-spin" />
                                ) : (
                                  <Trash2 className="h-3.5 w-3.5" />
                                )}
                                Close
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </main>
        </div>
      </div>

      {drawerVolume ? (
        <StorageDataDrawer
          volume={drawerVolume}
          onClose={() => setDrawerVolume(null)}
          onChanged={() => void refreshStorage(false)}
        />
      ) : null}
      {closeVolumeTarget ? (
        <CloseVolumeDialog
          volume={closeVolumeTarget}
          closing={closingVolumeID === closeVolumeTarget.id}
          onCancel={() => {
            if (!closingVolumeID) setCloseVolumeTarget(null);
          }}
          onConfirm={() => void closeVolume(closeVolumeTarget)}
        />
      ) : null}
    </>
  );
}

function CloseVolumeDialog({
  volume,
  closing,
  onCancel,
  onConfirm,
}: {
  volume: StorageVolume;
  closing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="compute-storage-drawer-backdrop">
      <button
        type="button"
        className="compute-storage-drawer-dismiss"
        onClick={onCancel}
        disabled={closing}
        aria-label="Cancel close volume"
      />
      <section className="compute-storage-close-modal" role="dialog" aria-modal="true" aria-label="Close storage volume">
        <header>
          <div className="compute-storage-close-icon">
            <Trash2 className="h-5 w-5" />
          </div>
          <button type="button" onClick={onCancel} disabled={closing} aria-label="Cancel">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div>
          <span>Close volume</span>
          <h2>{volume.name}</h2>
          <p>
            This deletes the provider-native volume and stops the hourly storage charge. Files that only exist on this volume may be lost.
          </p>
        </div>
        <div className="compute-storage-close-facts">
          <InfoRow label="Provider" value={volume.provider} />
          <InfoRow label="Volume ID" value={volume.providerVolumeID || volume.id} />
          <InfoRow label="Size" value={`${volume.sizeGb.toLocaleString()} GB`} />
          <InfoRow label="Usage" value={`${formatBytes(volume.usedBytes)} used / ${formatBytes(volume.availableBytes)} available`} />
          <InfoRow label="Hourly cost" value={`${formatUSD(volume.pricePerHour, 4)} / h`} />
          <InfoRow label="Files" value={volume.files.toLocaleString()} />
        </div>
        <footer>
          <button type="button" className="compute-storage-secondary-button" onClick={onCancel} disabled={closing}>
            Cancel
          </button>
          <button type="button" className="compute-storage-confirm-close-button" onClick={onConfirm} disabled={closing}>
            {closing ? <Loader2 className="h-3.5 w-3.5 compute-storage-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            Close volume
          </button>
        </footer>
      </section>
    </div>
  );
}

function StorageDataDrawer({
  volume,
  onClose,
  onChanged,
}: {
  volume: StorageVolume;
  onClose: () => void;
  onChanged: () => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<StorageFile[]>([]);
  const [queuedFiles, setQueuedFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState("");
  const providerWriteTarget = storageDataProviderForVolume(volume);
  const canCommitToProviderVolume = STORAGE_FILE_TRANSFER_PROVIDER_SLUGS.has(providerWriteTarget);

  async function refreshFiles() {
    setLoading(true);
    try {
      const payload = await apiRequest<{ items?: BackendStorageItem[] }>(`/api/v1/storage/volumes/${volume.id}/data`, {
        timeoutMs: 20_000,
        cache: "no-store",
      });
      setFiles((payload.items || []).map(mapStorageFile));
    } catch (err) {
      toast.error(apiErrorMessage(err, "Files could not be loaded."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refreshFiles();
  }, [volume.id]);

  useEffect(() => {
    if (!files.some((file) => file.providerStatus === "syncing")) return;
    const timer = window.setInterval(() => void refreshFiles(), 5_000);
    return () => window.clearInterval(timer);
  }, [files, volume.id]);

  function addFiles(incoming: FileList | File[]) {
    setQueuedFiles((current) => appendUniqueFiles(current, Array.from(incoming)));
  }

  async function uploadQueuedFiles() {
    if (queuedFiles.length === 0) return;
    if (!canCommitToProviderVolume) {
      toast.error("This provider can create volumes, but direct file writes into those volumes are not wired yet.");
      return;
    }
    setUploading(true);
    try {
      for (const file of queuedFiles) {
        setProgress(`Preparing ${file.name}`);
        const artifactRef = await uploadStorageFile(file, setProgress);
        setProgress(`Staging ${file.name} and starting provider sync`);
        await apiRequest(`/api/v1/storage/volumes/${volume.id}/data`, {
          method: "POST",
          body: {
            artifact_ref: artifactRef,
            filename: file.name,
            provider: providerWriteTarget,
          },
          timeoutMs: Math.max(120_000, Math.ceil(file.size / 50)),
          cache: "no-store",
        });
      }
      toast.success("Files staged. Provider sync is running.");
      setQueuedFiles([]);
      await refreshFiles();
      onChanged();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Upload failed."));
    } finally {
      setProgress("");
      setUploading(false);
    }
  }

  async function deleteStoredFile(file: StorageFile) {
    try {
      await apiRequest(`/api/v1/storage/volumes/${volume.id}/data/${file.id}`, {
        method: "DELETE",
        timeoutMs: 20_000,
        cache: "no-store",
      });
      toast.success("File removed.");
      await refreshFiles();
      onChanged();
    } catch (err) {
      toast.error(apiErrorMessage(err, "File could not be removed."));
    }
  }

  return (
    <div className="compute-storage-drawer-backdrop">
      <button
        type="button"
        className="compute-storage-drawer-dismiss"
        onClick={onClose}
        aria-label="Close storage files"
      />
      <section className="compute-storage-drawer" role="dialog" aria-modal="true" aria-label="Storage files">
        <header>
          <div>
            <span>Storage files</span>
            <h2>{volume.name}</h2>
            <p>
              {volume.typeLabel} / {volume.sizeGb.toLocaleString()} GB / {volume.provider}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div
          className={cn("compute-storage-dropzone", !canCommitToProviderVolume && "compute-storage-dropzone-disabled")}
          onDragOver={(event) => {
            event.preventDefault();
            event.dataTransfer.dropEffect = canCommitToProviderVolume ? "copy" : "none";
          }}
          onDrop={(event) => {
            event.preventDefault();
            if (!canCommitToProviderVolume) return;
            addFiles(event.dataTransfer.files);
          }}
          role="button"
          tabIndex={0}
          onClick={() => {
            if (canCommitToProviderVolume) inputRef.current?.click();
          }}
          onKeyDown={(event) => {
            if (canCommitToProviderVolume && (event.key === "Enter" || event.key === " ")) inputRef.current?.click();
          }}
          aria-disabled={!canCommitToProviderVolume}
        >
          <Upload className="h-5 w-5" />
          <div>
            <strong>Upload data</strong>
            <span>
              {canCommitToProviderVolume
                ? "Files are staged first, then written to this provider volume."
                : "This provider can create volumes, but direct file writes are not wired yet."}
            </span>
            <small>Provider write target: {providerWriteTarget}</small>
          </div>
          <input
            ref={inputRef}
            multiple
            type="file"
            disabled={!canCommitToProviderVolume}
            onChange={(event) => {
              if (event.target.files) addFiles(event.target.files);
              event.currentTarget.value = "";
            }}
          />
        </div>

        {queuedFiles.length > 0 ? (
          <div className="compute-storage-queue">
            <div>
              {queuedFiles.map((file) => (
                <span key={fileKey(file)}>
                  <span>{file.name}</span>
                  <small>{formatBytes(file.size)}</small>
                </span>
              ))}
            </div>
            <div className="compute-storage-queue-actions">
              <p>{uploading ? progress || "Uploading..." : `${queuedFiles.length} file${queuedFiles.length === 1 ? "" : "s"} queued`}</p>
              <div>
                <button
                  type="button"
                  onClick={() => setQueuedFiles([])}
                  disabled={uploading}
                  className="compute-storage-secondary-button"
                >
                  <X className="h-3.5 w-3.5" />
                  Clear
                </button>
                <button type="button" onClick={() => void uploadQueuedFiles()} disabled={uploading || !canCommitToProviderVolume}>
                  {uploading ? <Loader2 className="h-3.5 w-3.5 compute-storage-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  Upload
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="compute-storage-files">
          {loading ? (
            <EmptyState icon={Loader2} title="Loading files..." spin />
          ) : files.length === 0 ? (
            <EmptyState icon={FileText} title="No files stored in this volume yet." />
          ) : (
            files.map((file) => (
              <div key={file.id} className="compute-storage-file-row">
                <FileText className="h-4 w-4" />
                <div>
                  <strong>{file.filename}</strong>
                  <span>
                    {formatBytes(file.sizeBytes)} / {timeAgo(file.createdAt)} / {file.providerPath || file.provider}
                  </span>
                </div>
                <span
                  className={cn("compute-storage-status compute-storage-file-status", storageFileStatusClass(file.providerStatus))}
                  title={file.providerError || storageFileStatusLabel(file.providerStatus)}
                >
                  {storageFileStatusLabel(file.providerStatus)}
                </span>
                <button type="button" onClick={() => downloadStorageFile(file)}>
                  <Download className="h-3.5 w-3.5" />
                  Retrieve
                </button>
                <button
                  type="button"
                  onClick={() => void deleteStoredFile(file)}
                  aria-label={`Remove ${file.filename}`}
                  className="compute-storage-delete-button"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

function StorageStat({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: LucideIcon;
  label: string;
  value: string | number;
  hint: string;
}) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
      <p><Icon className="h-3.5 w-3.5" /> {hint}</p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="compute-storage-field">
      <span>{label}</span>
      {children}
    </label>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="compute-storage-route-row">
      <span>{label}</span>
      <strong>{value || "-"}</strong>
    </div>
  );
}

function StorageUsageCell({ volume }: { volume: StorageVolume }) {
  const usedPercent = volume.capacityBytes > 0
    ? Math.min(100, Math.max(0, (volume.usedBytes / volume.capacityBytes) * 100))
    : 0;
  return (
    <div className="compute-storage-usage-cell">
      <strong>{formatBytes(volume.usedBytes)} used</strong>
      <small>{formatBytes(volume.availableBytes)} available of {formatBytes(volume.capacityBytes)}</small>
      <span className="compute-storage-usage-bar" aria-hidden="true">
        <span style={{ width: `${usedPercent}%` }} />
      </span>
    </div>
  );
}

function EmptyState({ icon: Icon, title, spin }: { icon: LucideIcon; title: string; spin?: boolean }) {
  return (
    <div className="compute-storage-empty">
      <Icon className={cn("h-5 w-5", spin && "compute-storage-spin")} />
      <p>{title}</p>
    </div>
  );
}

async function loadStorageRoutes(): Promise<StorageRouteOption[]> {
  const [backendRoutes, providerRoutes, proRoutes] = await Promise.all([
    apiRequest<{ routes?: BackendStorageRoute[] }>("/api/v1/storage/routes", {
      method: "GET",
      timeoutMs: 12_000,
      cacheMaxAgeMs: 30_000,
    })
      .then((payload) => storageRoutesFromBackend(payload.routes || []))
      .catch(() => []),
    apiRequest<{ sources?: ProviderSourcePublic[] }>("/admin/v1/local/provider-sources", {
      method: "GET",
      timeoutMs: 12_000,
      cacheMaxAgeMs: 30_000,
    })
      .then((payload) => storageRoutesFromSources(payload.sources || []))
      .catch(() => []),
    apiRequest<{ providers?: ProProviderStorageRoute[] }>("/api/wallet/pro/providers", {
      method: "GET",
      timeoutMs: 12_000,
      cacheMaxAgeMs: 30_000,
    })
      .then((payload) => storageRoutesFromProProviders(payload.providers || []))
      .catch(() => []),
  ]);

  return appendUniqueRoutes([...backendRoutes, ...providerRoutes, ...proRoutes])
    .filter((route) => isVirtualManagedStorageRoute(route))
    .map(managedStorageRouteOption);
}

async function loadAvailableStorageCreditMicros(): Promise<number> {
  const payload = await apiRequest<BackendCreditBalance>("/api/wallet/balance", {
    method: "GET",
    timeoutMs: 12_000,
    cacheMaxAgeMs: 15_000,
  });
  return Math.max(0, Number(payload.available_credit_micros || 0));
}

function storageRoutesFromBackend(routes: BackendStorageRoute[]): StorageRouteOption[] {
  return routes
    .map((route): StorageRouteOption | null => {
      const managed = Boolean(route.managed || route.genrl_managed);
      const sourceSlug = normalizeRouteToken(route.source_slug) || (managed ? "genrl" : "");
      const providerSlug = normalizeRouteToken(route.provider_slug) || sourceSlug || (managed ? "genrl" : "");
      const routeID = String(route.route_id || (managed ? MANAGED_STORAGE_ROUTE_ID : synthesizeRouteID(sourceSlug, providerSlug))).trim();
      if (!sourceSlug || !routeID) return null;
      return {
        id: routeID,
        routeID,
        sourceSlug,
        providerSlug,
        label: String(route.label || providerSlug || sourceSlug).trim() || titleCase(sourceSlug),
        detail: String(route.detail || `${titleCase(providerSlug || sourceSlug)} storage`).trim(),
        driver: String(route.driver || providerSlug || "provider").trim(),
        managed,
      } satisfies StorageRouteOption;
    })
    .filter((route): route is StorageRouteOption => route !== null);
}

function storageRoutesFromSources(sources: ProviderSourcePublic[]): StorageRouteOption[] {
  return sources
    .filter((source) => source.active !== false)
    .map((source): StorageRouteOption | null => {
      const sourceSlug = normalizeRouteToken(source.source_slug);
      const providerSlug = normalizeRouteToken(source.provider_slug) || sourceSlug;
      const routeID = String(source.route_id || synthesizeRouteID(sourceSlug, providerSlug)).trim();
      const baseURL = String(source.provision_base_url || source.base_url || "").trim();
      if (!sourceSlug || !baseURL) return null;
      return {
        id: routeID || sourceSlug,
        routeID,
        sourceSlug,
        providerSlug,
        label: String(source.display_name || providerSlug || sourceSlug).trim() || titleCase(sourceSlug),
        detail: `${titleCase(source.driver || "provider")} storage`,
        driver: String(source.driver || "provider").trim(),
      } satisfies StorageRouteOption;
    })
    .filter((route): route is StorageRouteOption => route !== null);
}

function appendUniqueRoutes(routes: StorageRouteOption[]) {
  const seen = new Set<string>();
  const out: StorageRouteOption[] = [];
  routes.forEach((route) => {
    const key = String(route.id || route.routeID || route.sourceSlug || route.label).toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(route);
  });
  return out;
}

function storageRoutesFromProProviders(providers: ProProviderStorageRoute[]): StorageRouteOption[] {
  return providers
    .map((provider): StorageRouteOption | null => {
      const id = String(provider.id || "").trim();
      const providerSlug = normalizeRouteToken(provider.provider_slug);
      const status = String(provider.status || "").trim().toLowerCase();
      if (!id) return null;
      if (!provider.has_credential || ["revoked", "suspended", "deleted"].includes(status)) return null;
      return {
        id: `wallet::${id}`,
        routeID: `wallet::${id}`,
        sourceSlug: normalizeRouteToken(id),
        providerSlug,
        label: String(provider.display_name || provider.provider_name || providerSlug).trim() || titleCase(providerSlug),
        detail: `${titleCase(providerSlug)} provider-native storage`,
        driver: providerSlug,
      } satisfies StorageRouteOption;
    })
    .filter((route): route is StorageRouteOption => route !== null);
}

function storageRouteQuery(route: StorageRouteOption) {
  const params = new URLSearchParams();
  if (route.routeID) params.set("route_id", route.routeID);
  else if (route.sourceSlug) params.set("source_slug", route.sourceSlug);
  else if (route.providerSlug) params.set("provider_slug", route.providerSlug);
  const query = params.toString();
  return query ? `?${query}` : "";
}

function storageVolumeRouteQuery(volume: StorageVolume) {
  const params = new URLSearchParams();
  if (volume.routeID) params.set("route_id", volume.routeID);
  if (volume.sourceSlug) params.set("source_slug", volume.sourceSlug);
  if (volume.providerSlug) params.set("provider_slug", volume.providerSlug);
  const query = params.toString();
  return query ? `?${query}` : "";
}

function sameStorageVolume(a: StorageVolume, b: StorageVolume) {
  return a.id === b.id && a.routeID === b.routeID && a.providerSlug === b.providerSlug;
}

function isVirtualManagedStorageRoute(route: Partial<StorageRouteOption>) {
  const routeID = String(route.routeID || route.id || "").trim().toLowerCase();
  const sourceSlug = normalizeRouteToken(route.sourceSlug);
  const providerSlug = normalizeRouteToken(route.providerSlug);
  const driver = String(route.driver || "").trim().toLowerCase();
  return (
    routeID === MANAGED_STORAGE_ROUTE_ID ||
    Boolean(route.managed) ||
    driver === "managed" ||
    (sourceSlug === "genrl" && providerSlug === "genrl")
  );
}

function managedStorageRouteOption(route: StorageRouteOption): StorageRouteOption {
  return {
    ...route,
    id: MANAGED_STORAGE_ROUTE_ID,
    routeID: MANAGED_STORAGE_ROUTE_ID,
    sourceSlug: "genrl",
    providerSlug: "genrl",
    label: MANAGED_STORAGE_LABEL,
    detail: MANAGED_STORAGE_DETAIL,
    driver: "managed",
    managed: true,
  };
}

function mapStorageKinds(
  payload: {
    volume_types?: string[];
    volume_type_pricing?: Array<{ volume_type?: string; price_per_gb_hour?: number }>;
  },
  providerLabel: string,
): StorageKind[] {
  const pricing = new Map<string, number>();
  const offered = new Map<string, string>();

  (payload.volume_type_pricing || []).forEach((row) => {
    const raw = String(row.volume_type || "").trim();
    if (raw) offered.set(storageKindKey(raw), raw);
    const price = Number(row.price_per_gb_hour || 0);
    if (raw && Number.isFinite(price) && price > 0) {
      pricing.set(storageKindKey(raw), price);
      pricing.set(raw.toLowerCase(), price);
    }
  });
  (payload.volume_types || []).forEach((value) => {
    const raw = String(value || "").trim();
    if (raw) offered.set(storageKindKey(raw), raw);
  });

  const sourceTypes = offered.size > 0 ? Array.from(offered.values()) : DEFAULT_KINDS.map((kind) => kind.volumeType);
  return sourceTypes.map((volumeType) => {
    const key = storageKindKey(volumeType);
    const fallback = DEFAULT_KINDS.find((kind) => kind.id === key || storageKindKey(kind.volumeType) === key);
    return {
      id: key,
      volumeType,
      label: fallback?.label || titleCase(volumeType),
      detail: fallback?.detail || `${providerLabel} volume`,
      pricePerGBHour: pricing.get(key) || pricing.get(volumeType.toLowerCase()) || fallback?.pricePerGBHour || 0,
      icon: fallback?.icon || Database,
    };
  });
}

function managedObjectStorageKinds(kinds: StorageKind[]): StorageKind[] {
  const objectKind = kinds.find((kind) => kind.id === "object" || normalizeStorageType(kind.volumeType) === "object") || DEFAULT_KINDS[0];
  return [
    {
      ...objectKind,
      id: "object",
      volumeType: objectKind.volumeType || "object",
      label: objectKind.label || "S3 object storage",
      detail: MANAGED_STORAGE_DETAIL,
      icon: Cloud,
    },
  ];
}

function mapBackendVolume(row: BackendVolume, kinds: StorageKind[], selectedRoute: StorageRouteOption): StorageVolume {
  const volumeType = String(row.volume_type || "").trim() || "volume";
  const typeKey = storageKindKey(volumeType);
  const kind = kinds.find((item) => item.id === typeKey || item.volumeType === volumeType)
    || DEFAULT_KINDS.find((item) => item.id === typeKey);
  const created = parseDate(row.created_at);
  const updated = parseDate(row.updated_at) || created || new Date();
  const sizeGb = Number(row.size_gb || row.size || 0);
  const capacityBytes = Math.max(0, sizeGb * BYTES_PER_GB);
  const usedBytes = Number(row.used_bytes ?? (Number(row.used_gb || 0) * BYTES_PER_GB));
  const availableBytesFromBackend = row.available_bytes ?? (row.available_gb !== undefined ? Number(row.available_gb || 0) * BYTES_PER_GB : undefined);
  const availableBytes = Number(availableBytesFromBackend ?? Math.max(0, capacityBytes - usedBytes));
  const pricePerGBHour = Number(row.price_per_gb_hour || kind?.pricePerGBHour || 0);
  const pricePerHour = Number(row.price_per_hour || (sizeGb * pricePerGBHour));
  const sourceSlug = normalizeRouteToken(row.source_slug) || selectedRoute.sourceSlug;
  const providerSlug = normalizeRouteToken(row.provider_slug) || selectedRoute.providerSlug || sourceSlug;
  const routeID = String(row.route_id || selectedRoute.routeID || synthesizeRouteID(sourceSlug, providerSlug)).trim();
  return {
    id: String(row.id || ""),
    providerVolumeID: String(row.provider_volume_id || ""),
    name: String(row.name || row.id || "Storage"),
    status: String(row.status || "available").toLowerCase(),
    sizeGb,
    type: volumeType,
    typeLabel: kind?.label || titleCase(volumeType),
    provider: displayProviderName(String(row.provider || providerSlug || sourceSlug || selectedRoute.label)),
    providerSlug,
    sourceSlug,
    routeID,
    region: String(row.region_name || selectedRoute.detail || "Storage").trim(),
    environment: String(row.environment_name || "").trim() || "default",
    pricePerHour,
    pricePerGBHour,
    files: Number(row.file_count ?? row.files_count ?? 0),
    capacityBytes,
    usedBytes: Math.max(0, usedBytes),
    availableBytes: Math.max(0, availableBytes),
    createdAt: created ? created.toLocaleString() : "-",
    updatedAt: updated ? updated.toLocaleString() : "-",
    updatedAtMs: updated ? updated.getTime() : 0,
  };
}

function applyVolumeUsage(volume: StorageVolume, usedBytes: number) {
  const cleanUsedBytes = Math.max(0, Number.isFinite(usedBytes) ? usedBytes : 0);
  volume.usedBytes = cleanUsedBytes;
  volume.availableBytes = Math.max(0, volume.capacityBytes - cleanUsedBytes);
}

function mapStorageFile(row: BackendStorageItem): StorageFile {
  const providerStatus = normalizeStorageFileStatus(row.provider_status);
  return {
    id: String(row.id || ""),
    filename: String(row.filename || "file"),
    provider: String(row.provider || STAGING_PROVIDER_LABEL),
    providerPath: String(row.provider_path || ""),
    providerStatus,
    providerError: String(row.provider_error || ""),
    sizeBytes: Number(row.size_bytes || 0),
    contentType: String(row.content_type || ""),
    createdAt: String(row.created_at || ""),
    updatedAt: String(row.updated_at || row.created_at || ""),
    downloadURL: String(row.download_url || ""),
  };
}

function normalizeStorageFileStatus(status: unknown) {
  const token = String(status || "stored").trim().toLowerCase();
  if (token === "failed" || token === "error") return "failed";
  if (token === "syncing" || token === "pending" || token === "queued" || token === "staged") return "syncing";
  return "stored";
}

function storageFileStatusLabel(status: string) {
  switch (normalizeStorageFileStatus(status)) {
    case "failed":
      return "Sync failed";
    case "syncing":
      return "Syncing";
    default:
      return "Stored";
  }
}

function storageFileStatusClass(status: string) {
  switch (normalizeStorageFileStatus(status)) {
    case "failed":
      return "compute-storage-status-error";
    case "syncing":
      return "compute-storage-status-pending";
    default:
      return "compute-storage-status-ok";
  }
}

function chooseEnvironment(environments: StorageEnvironment[]) {
  return environments[0]?.name || "";
}

function storageEnvironmentsForRoute(_route: StorageRouteOption, environments: StorageEnvironment[]) {
  if (isVirtualManagedStorageRoute(_route) && environments.length === 0) {
    return [{ name: MANAGED_STORAGE_ENVIRONMENT, region: "GENRL managed" }];
  }
  if (environments.length > 0) return environments;
  return environments;
}

function storageEnvironmentForRoute(route: StorageRouteOption, preferredEnvironment: string) {
  const clean = String(preferredEnvironment || "").trim();
  if (clean) return clean;
  if (isVirtualManagedStorageRoute(route)) return MANAGED_STORAGE_ENVIRONMENT;
  return "";
}

async function uploadStorageFile(file: File, setProgress: (value: string) => void) {
  const session = await apiRequest<WeightUploadSession>("/api/v1/weight-uploads", {
    method: "POST",
    body: { artifact_kind: "data", expires_in_s: 86_400 },
    timeoutMs: 20_000,
    cache: "no-store",
  });
  const uploadURL = String(session.upload_url || "").trim();
  const artifactRef = String(session.artifact_ref || "").trim();
  if (!uploadURL || !artifactRef) {
    throw new APIError(500, "ERR_UPLOAD_SESSION_INVALID", "GENRL could not create an upload session.");
  }
  const maxSizeBytes = Number(session.max_size_bytes || 0);
  if (maxSizeBytes > 0 && file.size > maxSizeBytes) {
    throw new APIError(
      413,
      "ERR_UPLOAD_TOO_LARGE",
      `${file.name} is ${formatBytes(file.size)}. This deployment accepts browser uploads up to ${formatBytes(maxSizeBytes)}.`,
    );
  }

  const chunkSize = normalizeUploadChunkSize(session.chunk_size_bytes);
  if (file.size === 0) {
    setProgress(`Uploading ${file.name}`);
    await apiRequest(uploadURL, {
      method: "PUT",
      rawBody: file,
      headers: { "Content-Type": file.type || "application/octet-stream" },
      timeoutMs: 120_000,
      cache: "no-store",
    });
    return artifactRef;
  }

  let offset = 0;
  while (offset < file.size) {
    const endExclusive = Math.min(offset + chunkSize, file.size);
    const chunk = file.slice(offset, endExclusive);
    const checksum = await sha256Hex(chunk);
    const percent = Math.max(1, Math.floor((endExclusive / file.size) * 100));
    setProgress(`Uploading ${file.name} - ${percent}%`);
    const status = await apiRequest<WeightUploadStatus>(uploadURL, {
      method: "PUT",
      rawBody: chunk,
      headers: {
        "Content-Type": file.type || "application/octet-stream",
        "Content-Range": `bytes ${offset}-${endExclusive - 1}/${file.size}`,
        "X-Chunk-SHA256": checksum,
      },
      timeoutMs: Math.max(60_000, Math.ceil(chunk.size / 50)),
      cache: "no-store",
    });
    const received = Number(status.received_bytes || 0);
    offset = received > offset ? received : endExclusive;
  }
  return artifactRef;
}

async function sha256Hex(blob: Blob) {
  const buffer = await blob.arrayBuffer();
  const digest = await window.crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function downloadStorageFile(file: StorageFile) {
  const url = resolveStorageDownloadURL(file.downloadURL);
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
}

function resolveStorageDownloadURL(path: string) {
  const value = String(path || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  const base = resolveAPIBaseURL().replace(/\/+$/, "");
  return `${base}${value.startsWith("/") ? value : `/${value}`}`;
}

function appendUniqueFiles(current: File[], incoming: File[]) {
  const next = [...current];
  const seen = new Set(next.map(fileKey));
  incoming.forEach((file) => {
    const key = fileKey(file);
    if (seen.has(key)) return;
    seen.add(key);
    next.push(file);
  });
  return next;
}

function normalizeUploadChunkSize(value?: number) {
  const fallback = 16 * 1024 * 1024;
  const size = Number(value || 0);
  if (!Number.isFinite(size) || size <= 0) return fallback;
  return Math.min(Math.max(Math.floor(size), 1024 * 1024), 64 * 1024 * 1024);
}

function storageKindKey(value?: string) {
  const normalized = normalizeStorageType(value);
  return normalized || "volume";
}

function normalizeStorageType(value?: string) {
  const token = String(value || "").trim().toLowerCase();
  if (!token) return "";
  if (token.includes("nvme")) return "nvme";
  if (token.includes("archive")) return "archive";
  if (token.includes("object")) return "object";
  if (token.includes("ssd")) return "ssd";
  return token.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function normalizeRouteToken(value?: string) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function synthesizeRouteID(sourceSlug: string, providerSlug: string) {
  const source = normalizeRouteToken(sourceSlug);
  const provider = normalizeRouteToken(providerSlug);
  if (source && provider) return `${source}::${provider}`;
  return source || provider || "";
}

function storageDataProviderForVolume(volume: StorageVolume) {
  const token = normalizeRouteToken(`${volume.providerSlug || volume.provider} ${volume.sourceSlug}`);
  if (token.includes("hyperstack")) return "hyperstack";
  if (token.includes("runpod")) return "runpod";
  if (token.includes("lambda")) return "lambda";
  if (token.includes("genrl") || token.includes("managed")) return "unsupported";
  return normalizeRouteToken(volume.providerSlug || volume.sourceSlug || volume.provider) || "unsupported";
}

function displayProviderName(value: string) {
  const token = String(value || "").trim();
  if (!token) return "Storage";
  if (token.toLowerCase() === "genrl") return "GENRL";
  return titleCase(token);
}

function fileKey(file: File) {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

function parseDate(value?: string) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function timeAgo(value?: string) {
  const date = parseDate(value);
  if (!date) return "just now";
  const mins = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60_000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function formatBytes(value?: number) {
  const bytes = Number(value || 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = bytes;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size >= 10 || unit === 0 ? size.toFixed(0) : size.toFixed(1)} ${units[unit]}`;
}

function formatUSD(value: number, digits = 2) {
  return `$${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
}

function titleCase(value: string) {
  return String(value || "")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatStatus(status: string) {
  const token = String(status || "available").trim().toLowerCase();
  if (token === "in-use" || token === "in_use" || token === "attached") return "Attached";
  return titleCase(token || "Available");
}

function isLiveStorageVolumeStatus(status: string) {
  const token = String(status || "available").trim().toLowerCase().replace(/[_\s]+/g, "-");
  return !["deleted", "deleting", "terminated", "terminating", "failed", "error", "unavailable", "offline"].includes(token);
}

function statusClass(status: string) {
  const token = String(status || "").toLowerCase();
  if (token.includes("fail") || token.includes("error")) return "compute-storage-status-error";
  if (token.includes("creating") || token.includes("pending") || token.includes("sync")) return "compute-storage-status-pending";
  return "compute-storage-status-ok";
}

function apiErrorMessage(err: unknown, fallback: string) {
  if (err instanceof APIError) return err.detail || err.message || fallback;
  if (err instanceof Error) return err.message || fallback;
  return fallback;
}
